import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

// import { useFormState, useWatch } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { ExtendedCache } from 'hooks/useGetCache';
import { selectors } from 'store';

const checkFormHasBeenUpdated = (
  form: StringIndexedObject,
  previousFormString: string,
) => {
  if (!form || !previousFormString) return false;
  const formString = JSON.stringify(form);
  return formString !== previousFormString;
};

/**
 * Hook to use caching
 */
export default function useCache(cacheKey: string, disabled = false) {
  const [enable, setEnable] = useState(false);

  const dispatch = useAppDispatch();
  const cache = useSelector(selectors.infoGetCache.getRawResponse) as
    | ExtendedCache
    | undefined;
  const error = useSelector(selectors.infoGetCache.getError);
  const isSubmitting = useSelector(selectors.infoGetCache.getIsSubmitting);
  const isLoading = useSelector(selectors.infoGetCache.getIsLoading);
  const hasFetched = useSelector(selectors.infoGetCache.getHasFetched);
  const handleApiError = useApiError('Error updating cache');

  const form = useWatch();
  const { isDirty } = useFormState();

  const formRef = useRef(JSON.stringify(form));

  const cacheTimerRef = useRef<NodeJS.Timer | null>(null);

  const submitCache = useCallback(
    (formValues: StringIndexedObject) => {
      const values = JSON.stringify(formValues);
      dispatch(
        actions.infoPutCache({
          body: {
            ...cache,
            [cacheKey]: values,
          },
          onError: (err) => {
            handleApiError(err);
            clearTimeout(cacheTimerRef.current as unknown as number);
            cacheTimerRef.current = setTimeout(() => {
              setEnable(true);
            }, 10000);
          },
          onSuccess: () => {
            clearTimeout(cacheTimerRef.current as unknown as number);
            cacheTimerRef.current = setTimeout(() => {
              setEnable(true);
            }, 10000);
          },
        }),
      );
    },
    [dispatch, cacheKey, handleApiError, cache],
  );

  const inputTimerRef = useRef<NodeJS.Timer | null>(null);
  useEffect(() => {
    if (!isDirty || !enable) return;
    if (!checkFormHasBeenUpdated(form, formRef.current)) return;
    clearTimeout(inputTimerRef.current as NodeJS.Timeout);
    inputTimerRef.current = setTimeout(() => {
      formRef.current = JSON.stringify(form);
      setEnable(false);
      submitCache(form);
    }, 3000);
  }, [isDirty, form, enable, submitCache]);

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (initRef.current || disabled) return;
    setTimeout(() => {
      setEnable(true);
    }, 5000);
    initRef.current = true;
  }, [disabled]);

  return {
    cache,
    error,
    isSubmitting,
    isLoading,
    hasFetched,
  };
}
